import { AxiosPromise } from 'axios';

import { mmHttp, MmRequestConfig } from './mmHttp';
import { readPage } from './pagination';
import {
  MM_RESULTS_BY_IDS_PATH,
  buildResultsByIdsParams,
  mapResultsResponse,
} from './resultsWire';
import { MmMonitoringResult } from '../types/domain';
import {
  MmPage,
  MmResultGroupAggregate,
  MmResultListFilters,
  MmResultsSelectCheckBoxesData,
} from '../types/dto';

const TASKS_PATH = '/tasks';
const RESULTS_PATH = '/results';

export function fetchResultGroups(
  taskId: string,
  config?: MmRequestConfig,
): AxiosPromise<{ items: MmResultGroupAggregate[]; monitoringLevel: string | null }> {
  return mmHttp.get(
    TASKS_PATH + '/' + encodeURIComponent(taskId) + '/results/groups',
    Object.assign({ mmSection: 'results/groups' }, config),
  );
}

export function fetchResults(
  tradingNetworkId: number,
  filters: Partial<MmResultListFilters> & { classificationId?: string | null },
  pageParams: { [key: string]: any },
  config?: MmRequestConfig,
): Promise<MmPage<MmMonitoringResult>> {
  const payload = Object.assign({ tradingNetworkId: tradingNetworkId }, filters);

  return mmHttp
    .post(RESULTS_PATH + '/search', payload, Object.assign({ params: pageParams, mmSection: 'results/list' }, config))
    .then((response) => readPage<MmMonitoringResult>(response.data));
}

export function fetchMonitoringResultsByIds(
  taskIds: string[] | null,
  subTaskIds: string[] | null,
  config?: MmRequestConfig,
): Promise<MmMonitoringResult[]> {
  const params = buildResultsByIdsParams(taskIds, subTaskIds);

  if (params === null) {
    return Promise.resolve([]);
  }

  return mmHttp
    .get(
      MM_RESULTS_BY_IDS_PATH,
      /** params кладём последним: иначе быстрые фильтры из config затирают taskIds и subTaskIds */
      Object.assign({}, config, { params: params, mmSection: 'results/byIds' }),
    )
    .then((response) => mapResultsResponse(response.data));
}

export function correctResultPrice(
  resultId: string,
  price: number,
  config?: MmRequestConfig,
): AxiosPromise<MmMonitoringResult> {
  return mmHttp.put(
    RESULTS_PATH + '/' + encodeURIComponent(resultId),
    { correctedPrice: price },
    Object.assign({ mmSection: 'results/correct' }, config),
  );
}

/** Значение delete допустимо только для Сплошного мониторинга */
export type MmBulkReviewAction = 'accept' | 'reject' | 'delete';

/** `comment` обязателен, когда `action` равен `reject` */
export function bulkReviewResults(
  taskId: string,
  action: MmBulkReviewAction,
  filters: { [key: string]: any },
  selectCheckBoxesData: MmResultsSelectCheckBoxesData,
  comment: string,
  config?: MmRequestConfig,
): AxiosPromise<{ affected: number }> {
  return mmHttp.post(
    TASKS_PATH + '/' + encodeURIComponent(taskId) + '/results/bulk-review',
    {
      action: action,
      filters: filters,
      selectCheckBoxesData: selectCheckBoxesData,
      comment: comment,
    },
    Object.assign({ mmSection: 'results/bulk-review' }, config),
  );
}

/** Ограничение по заданиям едет в filters.taskIds: без него действие затронет чужие задания */
export function bulkReviewBatchResults(
  action: MmBulkReviewAction,
  filters: { [key: string]: any },
  selectCheckBoxesData: MmResultsSelectCheckBoxesData,
  comment: string,
  config?: MmRequestConfig,
): AxiosPromise<{ affected: number }> {
  return mmHttp.post(
    RESULTS_PATH + '/bulk-review',
    {
      action: action,
      filters: filters,
      selectCheckBoxesData: selectCheckBoxesData,
      comment: comment,
    },
    Object.assign({ mmSection: 'results/bulk-review' }, config),
  );
}

export function acceptTaskResults(
  taskId: string,
  config?: MmRequestConfig,
): AxiosPromise<{ operationId: string | null }> {
  return mmHttp.post(
    TASKS_PATH + '/' + encodeURIComponent(taskId) + '/accept',
    {},
    Object.assign({ mmSection: 'results/accept' }, config),
  );
}

export function fetchPhoto(photoId: string, config?: MmRequestConfig): AxiosPromise<Blob> {
  return mmHttp.get(
    '/photos/' + encodeURIComponent(photoId),
    Object.assign({ responseType: 'blob', mmSection: 'results/photo' }, config),
  );
}

/**
 * Доказательный кадр записи прайс-чекера. Не путать с фото ценника:
 * путь другой, id — записи, не строки `photos`.
 */
export function fetchPriceCheckerPhoto(
  entryId: string,
  kind: 'photo' | 'skip',
  config?: MmRequestConfig,
): AxiosPromise<Blob> {
  const suffix = kind === 'skip' ? '/skip-photo' : '/photo';
  return mmHttp.get(
    '/price-entries/' + encodeURIComponent(entryId) + suffix,
    Object.assign({ responseType: 'blob', mmSection: 'results/price-checker-photo' }, config),
  );
}

export function fetchThumbnail(photoId: string, config?: MmRequestConfig): AxiosPromise<Blob> {
  return mmHttp.get(
    '/photos/' + encodeURIComponent(photoId) + '/thumbnail',
    Object.assign({ responseType: 'blob', mmSection: 'results/photo-thumbnail' }, config),
  );
}
